import { useRef } from 'react';
import { useTracking } from './useTracking';
import { EVENTS } from '@habity/constants';

interface UseSearchTrackingOptions {
  /**
   * Minimum length of query before tracking
   */
  minQueryLength?: number;
  
  /**
   * Debounce time in milliseconds
   */
  debounceTime?: number;
  
  /**
   * Event name to track
   */
  eventName?: string;
}

/**
 * Hook for tracking search queries with debounce
 * 
 * @example
 * ```tsx
 * const { trackSearch } = useSearchTracking();
 * 
 * // In your component
 * <input onChange={(e) => {
 *   const query = e.target.value;
 *   trackSearch(query);
 * }} />
 * ```
 */
export const useSearchTracking = ({
  minQueryLength = 3,
  debounceTime = 500,
  eventName = EVENTS.SEARCHED_LOCATION,
}: UseSearchTrackingOptions = {}) => {
  const { track } = useTracking();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastQueryRef = useRef<string>('');

  /**
   * Track a search query with debounce
   * 
   * @param query The search query to track
   * @param metadata Additional metadata to include with the event
   */
  const trackSearch = (query: string, metadata: Record<string, unknown> = {}) => {
    // Don't track if query is too short
    if (!query || query.length < minQueryLength) {
      return;
    }
    
    // If the query hasn't changed, don't reset the timer
    if (query === lastQueryRef.current) {
      return;
    }
    
    // Update the current query reference
    lastQueryRef.current = query;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set a new timer
    timerRef.current = setTimeout(() => {
      track(eventName, { query, ...metadata });
      timerRef.current = null;
    }, debounceTime);
  };

  /**
   * Reset tracking state
   */
  const resetTracking = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    lastQueryRef.current = '';
  };

  return {
    trackSearch,
    resetTracking
  };
};
